import { Currency } from "../../../../../app-settings/localesAndCurrencies";
import { useTranslation } from "react-i18next";
import { useIntlDurationFormat } from "hooks/useIntlDurationFormat";
import {
  defaultBrazilCompensation,
  defaultCompensation,
} from "../../../../../constants/compensation";
import { Box, Divider, Flex, FlexProps } from "@chakra-ui/react";
import { Collapsible } from "@airhelp/react";
import { MoneyPaper, Time } from "@airhelp/icons";
import { FlightSummaryHeader } from "./FlightSummaryHeader";
import { FlightSummaryRoute } from "./FlightSummaryRoute";
import {
  FlightDetailsArrivalSection,
  FlightDetailsSectionText,
  FlightDetailsTextSection,
} from "./elements";
import { FlightSummaryAdditionalInfo } from "./FlightSummaryAdditionalInfo";
import { isMobile } from "react-device-detect";
import { toCurrency } from "v2/utils/toCurrency";
import { appSettingsSnapshot } from "v2/settings";
import { parseDate } from "../../../../utils/parseDate";

export function DisruptedFlightSummary({
  currency,
  disruptionReason,
  arrivalTime,
  actualArrivalTime,
  departureAirportIata,
  departureAirportCity,
  arrivalAirportIata,
  arrivalAirportCity,
  airlineName,
  airlineCode,
  flightNumber,
  delayMins,
  distanceKm,
  potentialCompensation,
  hideCompensation,
  isRtl,
  ...props
}: FlexProps & {
  currency: Currency;
  disruptionReason: "delayed" | "cancelled";
  hideCompensation?: boolean;
  arrivalTime?: string;
  actualArrivalTime?: string;
  departureAirportIata: string;
  departureAirportCity: string;
  arrivalAirportIata: string;
  arrivalAirportCity: string;
  airlineName?: string;
  airlineCode?: string;
  flightNumber?: string;
  delayMins?: number;
  distanceKm?: number;
  potentialCompensation?: number;
  isRtl: boolean;
}) {
  const { t } = useTranslation();
  const { locale } = appSettingsSnapshot();

  const totalDelay = useIntlDurationFormat({
    locale,
    delayMins: delayMins || 0,
  });

  const compensation = toCurrency(
    typeof potentialCompensation === "number" && potentialCompensation > 0
      ? potentialCompensation
      : defaultCompensation,
    currency,
    locale,
  );

  return (
    <Flex
      direction="column"
      justify="left"
      border="1px solid"
      borderColor="greyscale.300"
      borderRadius="lg"
      {...props}
    >
      <Flex direction="column">
        <Flex direction="column" alignItems="flex-start">
          {disruptionReason === "delayed" && (
            <>
              <FlightSummaryHeader
                data-testid="delayed-flight-header"
                airlineName={airlineName}
                airlineCode={airlineCode}
                flightNumber={flightNumber}
                disruptionReason={disruptionReason}
              />
              <Divider flex={1} borderColor="greyscale.300" />
            </>
          )}
          <Box mb={{ base: 6, md: 8 }} />
          <FlightSummaryRoute
            departureAirportCity={departureAirportCity}
            departureAirportIata={departureAirportIata}
            arrivalAirportIata={arrivalAirportIata}
            arrivalAirportCity={arrivalAirportCity}
            isRtl={isRtl}
          />
          {disruptionReason === "cancelled" && (
            <>
              <Divider flex={1} borderColor="greyscale.300" />
              <FlightSummaryHeader
                data-testid="cancelled-flight-header"
                airlineName={airlineName}
                airlineCode={airlineCode}
                flightNumber={flightNumber}
                disruptionReason={disruptionReason}
              />
              <Divider flex={1} mb={6} borderColor="greyscale.300" />
            </>
          )}
          <Flex
            direction={{ base: "column", md: "row" }}
            justifyContent={{ base: "center", md: "space-between" }}
            px={{ base: 4, md: 6 }}
            pb={{ base: 4, md: 8 }}
            gap={6}
            w="full"
          >
            {disruptionReason === "delayed" && (
              <FlightSummaryAdditionalInfo
                data-testid="total-delay"
                boxBackground="primary.100"
                title={t("steps.barcode.delayed.ec261.total_delay")}
                icon={<Time boxSize={5} color="primary.400" />}
                description={totalDelay}
              />
            )}
            {!hideCompensation && currency === "BRL" && (
              <FlightSummaryAdditionalInfo
                data-testid="total-compensation"
                boxBackground="success.100"
                title={t("steps.disrupted_flight_detected.potential_airline_compensation")}
                icon={<MoneyPaper boxSize={4} color="success.500" />}
                description={t("steps.disrupted_flight_detected.brazil_compensation", {
                  compensation: toCurrency(defaultBrazilCompensation, currency, locale),
                })}
              />
            )}
            {!hideCompensation && currency !== "BRL" && (
              <FlightSummaryAdditionalInfo
                data-testid="total-compensation"
                boxBackground="success.100"
                icon={<MoneyPaper boxSize={4} color="success.500" />}
                title={t("steps.disrupted_flight_detected.potential_airline_compensation")}
                description={t("steps.barcode.delayed.ec261.compensation.description", {
                  compensation,
                })}
              />
            )}
          </Flex>
        </Flex>
        <Divider flex={1} mt={4} borderColor="greyscale.300" m={0} />
        <DisruptedFlightSummary.Details
          arrivalTime={parseDate(arrivalTime)}
          actualArrivalTime={parseDate(actualArrivalTime)}
          disruptionReason={disruptionReason}
          delayMins={delayMins}
          distanceKm={distanceKm}
        />
      </Flex>
    </Flex>
  );
}

DisruptedFlightSummary.Details = function FlightSummaryDetails({
  arrivalTime,
  actualArrivalTime,
  disruptionReason,
  delayMins,
  distanceKm,
}: {
  arrivalTime?: Date;
  actualArrivalTime?: Date;
  disruptionReason: "delayed" | "cancelled";
  delayMins?: number;
  distanceKm?: number;
}) {
  const { t } = useTranslation();

  const Content = () => (
    <Flex
      gap={{ base: 6, md: 6 }}
      mt={{ base: 2, md: 0 }}
      direction="column"
      maxH={{ base: "auto", md: "190px" }}
      flexWrap="wrap"
      w="full"
    >
      {arrivalTime && (
        <FlightDetailsArrivalSection
          label={t("steps.barcode.delayed.ec261.scheduled_arrival_time")}
          arrivalDate={arrivalTime}
        />
      )}
      {actualArrivalTime && disruptionReason === "delayed" && (
        <FlightDetailsArrivalSection
          data-testid="delayed-flight-arrival"
          wasArrivalDelayed={typeof delayMins !== "undefined" && delayMins > 0}
          label={t("steps.barcode.delayed.ec261.actual_arrival_time")}
          arrivalDate={actualArrivalTime}
        />
      )}
      {disruptionReason === "cancelled" && (
        <FlightDetailsTextSection
          data-testid="cancelled-flight-arrival"
          label={t("steps.barcode.delayed.ec261.actual_arrival_time")}
          text={
            <FlightDetailsSectionText color="secondary.700">
              {t("steps.disrupted_flight_detected.cancelled.never_arrived")}
            </FlightDetailsSectionText>
          }
        />
      )}
      {distanceKm && (
        <FlightDetailsTextSection
          label={t("steps.barcode.delayed.ec261.flight_distance")}
          text={t("units.km", { distance: distanceKm })}
        />
      )}
    </Flex>
  );

  return (
    <Flex data-testid="flight-details">
      {isMobile ? (
        <Collapsible
          title={t("steps.disrupted_flight_detected.flight_details")}
          variant="secondary"
          bgColor="transparent"
          data-testid="flight-details-collapsible"
        >
          <Content />
        </Collapsible>
      ) : (
        <Flex px={6} py={8} w={{ base: "auto", md: "100%" }}>
          <Content />
        </Flex>
      )}
    </Flex>
  );
};
